/**
 * Normalisation of one RPE event list into an IR event list (W1-07a formats-json-rpe; DESIGN.md §7.2, F§6.2,
 * F§7.2-7.3, F§9.7).
 *
 * The IR only holds sorted, non-overlapping, positive-length lists. Anything else is rebuilt to reproduce what
 * Phira shows when it plays the file FORWARD (its `Anim` cursor walk, F§6.2):
 *
 *  - keyframes are taken in file order, two per event (start: the event's curve; end: hold), plus the default
 *    keyframe Phira prepends for colour / text / incline / paint when the first event does not start at beat 0;
 *  - playing forward from t = 0, the cursor at time t is the longest prefix of keyframes (from index 1) whose times
 *    are all <= t; the value is the cursor keyframe's curve (a start keyframe: its event's curve, an end or
 *    default keyframe: hold). This only changes at keyframe times, so the whole function is a sequence of
 *    pieces "curve of event E" / "hold value v" between breakpoints;
 *  - the pieces are written back as events: a curve piece that begins after its event's start becomes the event's
 *    right part, sliced on its own curve by seconds progress (Ease.splitAt; exactness reported); a hold piece whose
 *    value differs from the previous event's end becomes a constant event (a jump; a 1-beat tail at the end); the
 *    leading default hold is left to Phira, which prepends the same default again.
 *  That yields exactly D§7.2's rules: overlapping events keep the earlier one intact and continue the later one on
 *  its own curve; a fully nested event becomes a jump to its end value at the earlier end; a zero-length (or
 *  reversed) event that is not first becomes a jump; a zero-length first event is reported because Phira showed
 *  NaN / infinity before it.
 *
 * Unsorted lists are stable-sorted by start beat first. They are 'normalized' only when the sorted list provably
 * plays like the file in Phira (same piece at every time >= 0, and at t = 0 where Phira's first evaluation keeps
 * cursor 0), else 'changed'. Speed lists differ by integrator (F§7.2-7.3): the legacy integrator plays speed
 * forward with linear ramps (easing ignored; unsorted is always 'changed'); the 1.7 integrator sorts by start and
 * COMPRESSES a later overlapping event into [previous end, its end] with its full curve, a zero-length result
 * acting as a jump to its end speed. Incline is frozen at its value at t = 0 (F§9.4), so only that value decides
 * whether a change is visible. GIF lists (F§9.7: natural-speed play between events and after them) keep the
 * earlier of two overlapping events and slice the later one like the other lists; an event with no length or
 * nested in another one makes Phira jump to its end value and play on at the GIF's own speed. Such a jump is
 * written only where it is invisible (the next event starts at that moment); anywhere else no event can express
 * it (a constant event would stop the GIF, and the GIF's frame timing is not part of the chart), so it is
 * dropped and reported as 'changed'.
 *
 * Default keyframes (colour, text, incline, paint): Phira prepends one at time 0 only when the first event in
 * FILE order does not start at beat 0 (F§6.2). Moving every beat by the first BPM point's beat (normTempo.ts) can
 * flip that condition; the list is then repaired so that Phira shows the same values (a constant default event
 * before beat 0, the first event cut at beat 0, or a filler reproducing a constant or linear lead-in), else the
 * difference is reported as 'changed'.
 *
 * Speed events with a Bezier curve: the curve is never written back (IR rule; the original fields go to the
 * sidecar). Under the 1.7 integrator an event with easingType >= 2 evaluates that curve: Phira panics unless a
 * non-speed event registered the same Bezier key (F§6.7), and with a registered key the floor positions follow
 * that curve, so removing it is 'changed' either way. Elsewhere the curve has no effect: 'normalized' in 1.7
 * mode, and in legacy mode a flagged row of the kept report (kept.ts, from the sidecar entries).
 *
 * Decisions use exact beats (Beat rationals); progress and values use Phira's seconds (raw triples through the
 * tempo map, F§3.1), so equal beats written as different triples never count as overlaps.
 *
 * Range (normRange.ts): an event whose shifted beats a triple cannot hold is refused ('changed'), and every beat
 * this module adds stays representable: a final jump's tail is 1 beat long, or a fraction of a beat at the end of
 * the beat range (a jump at the very last beat has no event form: 'changed'); the default pad before a first event
 * and the nodes of a baked curve are chosen the same way. Values beyond the f32 range (`1e39`, an f32 infinity in
 * Phira) become the largest f32 of their sign ('normalized', one entry per list).
 */

import { Ease } from '../../core/ease/index';
import { stringTweenDetailed, stripCounterMarks } from '../../core/ease/stringTween';
import type { BezierPoints, Ease as EaseValue, SplitResult } from '../../core/ease/types';
import type { IrEvent, IrEventOrigin } from '../../core/ir/types';
import { Beats } from '../../core/time/beat';
import type { Beat, Triple } from '../../core/time/types';
import type { Passthrough, RGB } from '../../core/util/types';
import type { RpeEvent } from './json';
import { clampF32, exceedsF32, toInt } from './normCoerce';
import { beatAfter, beatBefore, beatBetween, beatLabel, fitsShifted, shiftedBeat } from './normRange';
import type { NormReport } from './normReport';
import type { EventListKey } from './schema';

const fr = Math.fround;

/** The counter mark of text events (F§9.3). */
const COUNTER_MARK = '%P%';

export type ListValue = number | RGB | string;
export type ListMode = 'standard' | 'speedLegacy' | 'speed170' | 'gif' | 'incline';
export type ValueKind = 'f32' | 'rgb' | 'string';

/** Time services for a list: seconds of source triples and output triples (beat shift applied). */
export interface ListTime {
  /** Phira seconds of a valid triple (a + b / c through the BPM list). */
  sec(t: Triple): number;
  /** Beat at chart time 0 (the first BPM point), in source beats. */
  readonly zeroBeat: Beat;
  /**
   * Output triple of a source triple whose exact beat is `beat` (raw triple kept when there is no shift). Only for
   * beats whose output fits (`fitsShifted(beat, shift)`).
   */
  out(t: Triple, beat: Beat): Triple;
  /** Beat subtracted from every source beat (null when there is none). */
  readonly shift: Beat | null;
}

export interface ListContext {
  readonly key: EventListKey | null;
  readonly kind: ValueKind;
  readonly mode: ListMode;
  /** Value Phira prepends at time 0 when the first event does not start at beat 0 (colour, text, incline, paint). */
  readonly def?: ListValue;
  /** JSON path of the list (report entries). */
  readonly path: string;
  readonly line: number | undefined;
  readonly time: ListTime;
  readonly report: NormReport;
  /** Sidecar key of the list for removed data (speed bezier fields); null where no sidecar exists (fragments). */
  readonly sidecarKey: string | null;
  /**
   * True when a non-speed event of the chart registers this Bezier curve's key in Phira's Bezier map (F§6.7), so a
   * 1.7 speed event with that curve does not make Phira panic. Absent (fragments): nothing is registered.
   */
  readonly bezierRegistered?: (points: BezierPoints) => boolean;
}

/** A validated source event (times in source beats and Phira seconds). */
interface Ev {
  readonly idx: number;
  readonly start: Beat;
  readonly end: Beat;
  readonly sTriple: Triple;
  readonly eTriple: Triple;
  readonly t0: number;
  readonly t1: number;
  readonly v0: ListValue;
  readonly v1: ListValue;
  readonly easingType: number;
  readonly easingLeft: number;
  readonly easingRight: number;
  readonly bezier: number;
  readonly bezierPoints: BezierPoints;
  readonly linkgroup: number;
  readonly extra: Passthrough;
}

/** An output event before conversion to IR (source beat space). */
interface OutEv {
  readonly sTriple: Triple;
  readonly sBeat: Beat;
  readonly eTriple: Triple;
  readonly eBeat: Beat;
  readonly t0: number;
  readonly t1: number;
  readonly v0: ListValue;
  readonly v1: ListValue;
  readonly easingType: number;
  readonly easingLeft: number;
  readonly easingRight: number;
  readonly bezier: number;
  readonly bezierPoints: BezierPoints;
  readonly linkgroup: number;
  readonly extra: Passthrough;
  readonly origin: IrEventOrigin;
}

const ZERO_POINTS: BezierPoints = [0, 0, 0, 0];

// ---------------------------------------------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------------------------------------------

export function valuesEqual(a: ListValue, b: ListValue): boolean {
  if (typeof a === 'number' || typeof b === 'number') return a === b;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function coerceValue(
  v: unknown,
  kind: ValueKind,
  clamps: F32Clamps,
  base: string,
  index: number,
  field: string,
): ListValue {
  if (kind === 'string') return typeof v === 'string' ? v : '';
  if (kind === 'rgb') {
    const c = Array.isArray(v) ? (v as readonly unknown[]) : [];
    const ch = (i: number): number => toInt(typeof c[i] === 'number' ? c[i] : undefined, 0, 255, 255);
    return [ch(0), ch(1), ch(2)];
  }
  return clamps.f32(typeof v === 'number' ? v : 0, base, index, field);
}

/**
 * Counts the finite numbers beyond the f32 range that become the largest f32 (Phira reads them as infinity) and
 * reports them as one 'normalized' entry (`rpe.report.valueClamped`, first path and field kept). Numbers beyond
 * the f64 range were reported by the decoder already.
 */
export class F32Clamps {
  private count = 0;
  private firstPath: string | undefined;
  private firstField = '';

  /** `v` clamped into the f32 range; it is at `${base}[${index}].${field}` (the path is built once). */
  f32(v: number, base: string, index: number, field: string): number {
    if (exceedsF32(v)) {
      this.count++;
      if (this.firstPath === undefined) {
        this.firstPath = `${base}[${String(index)}].${field}`;
        this.firstField = field;
      }
    }
    return clampF32(v);
  }

  report(report: NormReport, line: number | undefined): void {
    if (this.count === 0) return;
    report.add(
      'normalized',
      { key: 'rpe.report.valueClamped', params: { field: this.firstField } },
      this.firstPath,
      line,
      this.count,
    );
  }
}

/** Phira's curve value y of an event at (seconds) progress p. */
function curveY(e: Ev, p: number, linear: boolean): number {
  if (linear) return fr(p);
  const desc = Ease.resolveRpe(e.easingType, e.easingLeft, e.easingRight, e.bezier, e.bezierPoints);
  return Ease.phiraY(desc, fr(p));
}

/**
 * Value of event `e` at seconds progress p (in source units); `exact` = representable without rounding; `finite`
 * = Phira computes a number there. Phira's clamped slice divides by f(R) - f(L), which is 0 for a degenerate slice
 * (F§6.6): the curve then gives NaN / infinity, which no event value can hold (text shows "NaN" and stays finite).
 * A finite number beyond the f32 range (a Back / Elastic overshoot or a lead-in extrapolated from values near the
 * f32 limit; Phira's f32 track shows infinity there) becomes the largest f32 of its sign and is not exact, so the
 * writer can write it and the caller reports an approximation.
 */
function valueAt(
  e: Ev,
  p: number,
  linear: boolean,
): { readonly v: ListValue; readonly exact: boolean; readonly finite: boolean; readonly textCase?: string } {
  const y = curveY(e, p, linear);
  if (typeof e.v0 === 'number' && typeof e.v1 === 'number') {
    const v = e.v0 + (e.v1 - e.v0) * y;
    const inF32 = Number.isFinite(Math.fround(v));
    return { v: inF32 ? v : clampF32(v), exact: inF32, finite: Number.isFinite(v) };
  }
  if (typeof e.v0 === 'string' && typeof e.v1 === 'string') {
    const r = stringTweenDetailed(e.v0, e.v1, y);
    // A counter keeps its %P% mark so the remaining part still counts (approximately: the shown value is rounded).
    const text = r.case === 'counter' ? `%P%${r.text}` : r.text;
    return { v: text, exact: r.case === 'switch' || r.case === 'empty', finite: true, textCase: r.case };
  }
  const a = e.v0 as RGB;
  const b = e.v1 as RGB;
  const raw = [0, 1, 2].map((i) => (a[i] ?? 0) + ((b[i] ?? 0) - (a[i] ?? 0)) * y);
  const finite = raw.every((c) => Number.isFinite(c));
  const exact = finite && raw.every((c) => Math.abs(c - Math.round(c)) < 1e-6);
  const rgb: RGB = [toInt(raw[0], 0, 255, 0), toInt(raw[1], 0, 255, 0), toInt(raw[2], 0, 255, 0)];
  return { v: rgb, exact, finite };
}

function modelEase(e: Ev): EaseValue {
  if (e.bezier !== 0) return { kind: 'bezier', p: e.bezierPoints };
  const id = Number.isInteger(e.easingType) && e.easingType >= 1 && e.easingType <= 29 ? e.easingType : 1;
  const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
  return { kind: 'tween', id, left: clamp01(fr(e.easingLeft)), right: clamp01(fr(e.easingRight)) };
}

function isConstant(e: Ev): boolean {
  return valuesEqual(e.v0, e.v1);
}

// ---------------------------------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------------------------------

function coerceEvents(events: readonly RpeEvent<ListValue>[], ctx: ListContext): Ev[] {
  const out: Ev[] = [];
  let refused = 0;
  let firstRefusedPath: string | undefined;
  let outOfRange = 0;
  let firstOutOfRangePath: string | undefined;
  let speedBezierInert = 0;
  let speedBezierShared = 0;
  let speedBezierCrash = 0;
  let bezierFlag = 0;
  const clamps = new F32Clamps();
  events.forEach((src, idx) => {
    const s = Beats.tryFromTriple(src.startTime);
    const e = Beats.tryFromTriple(src.endTime);
    if (s === null || e === null) {
      refused++;
      firstRefusedPath ??= `${ctx.path}[${String(idx)}]`;
      return;
    }
    if (!fitsShifted(s.beat, ctx.time.shift) || !fitsShifted(e.beat, ctx.time.shift)) {
      // Once the chart starts at beat 0 at its first BPM point, a triple cannot hold this event's beats.
      outOfRange++;
      firstOutOfRangePath ??= `${ctx.path}[${String(idx)}]`;
      return;
    }
    let bezier = toInt(src.bezier, 0, 255, 0);
    const bp = src.bezierPoints;
    let bezierPoints: BezierPoints = bp
      ? [
          clamps.f32(bp[0], ctx.path, idx, 'bezierPoints'),
          clamps.f32(bp[1], ctx.path, idx, 'bezierPoints'),
          clamps.f32(bp[2], ctx.path, idx, 'bezierPoints'),
          clamps.f32(bp[3], ctx.path, idx, 'bezierPoints'),
        ]
      : ZERO_POINTS;
    const easingType = toInt(src.easingType, -2147483648, 2147483647, 1);
    if (bezier !== 0 && (ctx.mode === 'speedLegacy' || ctx.mode === 'speed170')) {
      // Never written on speed (Phira's 1.7 integrator panics on an unregistered bezier key, F§6.7): the
      // original bezier fields stay in phiedit.json only (D§6.3).
      if (ctx.mode === 'speed170' && easingType >= 2) {
        // The 1.7 integrator evaluates the curve (F§7.3): a panic, or floor positions that follow it.
        if (ctx.bezierRegistered?.(bezierPoints) === true) speedBezierShared++;
        else speedBezierCrash++;
      } else if (ctx.mode === 'speed170' || ctx.sidecarKey === null) {
        // Phira does not use the curve here. In legacy mode a whole chart keeps it in the sidecar and the kept
        // report flags it; a fragment has no sidecar, so its text loses the curve: reported.
        speedBezierInert++;
      }
      if (ctx.sidecarKey !== null && ctx.line !== undefined) {
        ctx.report.toSidecar(ctx.line, `${ctx.sidecarKey}[${String(idx)}].bezier`, {
          bezier,
          bezierPoints: [...bezierPoints],
        });
      }
      bezier = 0;
      bezierPoints = ZERO_POINTS;
    } else if (bezier > 1) {
      // Phira (and RPE) only test `bezier != 0`; the document keeps a flag, so the export writes 1.
      bezierFlag++;
      bezier = 1;
    }
    out.push({
      idx,
      start: s.beat,
      end: e.beat,
      sTriple: src.startTime,
      eTriple: src.endTime,
      t0: ctx.time.sec(src.startTime),
      t1: ctx.time.sec(src.endTime),
      v0: coerceValue(src.start, ctx.kind, clamps, ctx.path, idx, 'start'),
      v1: coerceValue(src.end, ctx.kind, clamps, ctx.path, idx, 'end'),
      easingType,
      easingLeft: clamps.f32(src.easingLeft ?? 0, ctx.path, idx, 'easingLeft'),
      easingRight: clamps.f32(src.easingRight ?? 1, ctx.path, idx, 'easingRight'),
      bezier,
      bezierPoints,
      linkgroup: toInt(src.linkgroup, -2147483648, 2147483647, 0),
      extra: src.extra,
    });
  });
  if (refused > 0) {
    ctx.report.add('changed', { key: 'rpe.report.eventInvalidTime' }, firstRefusedPath, ctx.line, refused);
  }
  if (outOfRange > 0) {
    ctx.report.add('changed', { key: 'rpe.report.eventBeatOutOfRange' }, firstOutOfRangePath, ctx.line, outOfRange);
  }
  clamps.report(ctx.report, ctx.line);
  if (speedBezierInert > 0) {
    ctx.report.add('normalized', { key: 'rpe.report.speedBezier' }, ctx.path, ctx.line, speedBezierInert);
  }
  if (bezierFlag > 0) {
    ctx.report.add('normalized', { key: 'rpe.report.bezierFlag' }, ctx.path, ctx.line, bezierFlag);
  }
  if (speedBezierShared > 0) {
    ctx.report.add('changed', { key: 'rpe.report.speedBezierShared' }, ctx.path, ctx.line, speedBezierShared);
  }
  if (speedBezierCrash > 0) {
    ctx.report.phiraWouldNotLoad();
    ctx.report.add('changed', { key: 'rpe.report.speedBezierCrash' }, ctx.path, ctx.line, speedBezierCrash);
  }
  return out;
}

function isWellFormed(evs: readonly Ev[]): boolean {
  for (let i = 0; i < evs.length; i++) {
    const e = evs[i];
    if (e === undefined || !Beats.lt(e.start, e.end)) return false;
    const prev = evs[i - 1];
    if (prev !== undefined && Beats.lt(e.start, prev.end)) return false;
  }
  return true;
}

function isUnsorted(evs: readonly Ev[]): boolean {
  for (let i = 1; i < evs.length; i++) {
    const a = evs[i - 1];
    const b = evs[i];
    if (a !== undefined && b !== undefined && Beats.lt(b.start, a.start)) return true;
  }
  return false;
}

function stableSortByStart(evs: readonly Ev[]): Ev[] {
  return evs
    .map((e, i) => [e, i] as const)
    .sort((x, y) => Beats.cmp(x[0].start, y[0].start) || x[1] - y[1])
    .map(([e]) => e);
}

// ---------------------------------------------------------------------------------------------------------------
// Forward-play pieces
// ---------------------------------------------------------------------------------------------------------------

interface Kf {
  readonly time: number;
  readonly beat: Beat;
  readonly triple: Triple;
  readonly ev: Ev | null;
  readonly role: 'start' | 'end' | 'default';
  readonly value: ListValue;
}

/**
 * What the cursor shows: an event's curve, or a held keyframe value. `last` = the held keyframe is the list's last
 * one: Phira then shows its value as is, while any other held text is tweened towards the next keyframe at
 * progress 0, which shows it without its %P% marks (F§9.3).
 */
type Piece =
  | { readonly type: 'curve'; readonly ev: Ev }
  | { readonly type: 'hold'; readonly value: ListValue; readonly kf: Kf; readonly last: boolean };

/**
 * Phira's keyframes of a list in the given order (two per event), with the default keyframe at time 0 when `def`
 * is given (the caller decides by Phira's rule: `withDefault`).
 */
function keyframes(list: readonly Ev[], def: ListValue | undefined, zeroBeat: Beat): Kf[] {
  const kfs: Kf[] = [];
  for (const e of list) {
    kfs.push({ time: e.t0, beat: e.start, triple: e.sTriple, ev: e, role: 'start', value: e.v0 });
    kfs.push({ time: e.t1, beat: e.end, triple: e.eTriple, ev: e, role: 'end', value: e.v1 });
  }
  if (def !== undefined && list.length > 0) {
    kfs.unshift({ time: 0, beat: zeroBeat, triple: Beats.toTriple(zeroBeat), ev: null, role: 'default', value: def });
  }
  return kfs;
}

/**
 * The default value Phira prepends for a list whose first event starts at `firstTriple` (as written in the file
 * Phira reads): only when that beat is not 0 (F§6.2). undefined for lists without a default.
 */
function withDefault(ctx: ListContext, firstTriple: Triple | undefined): ListValue | undefined {
  return ctx.def !== undefined && firstTriple !== undefined && Beats.tripleToF64(firstTriple) !== 0
    ? ctx.def
    : undefined;
}

/** Keyframes of the SOURCE list as Phira builds them (file order; default by the source triple). */
function sourceKeyframes(evs: readonly Ev[], ctx: ListContext): Kf[] {
  return keyframes(evs, withDefault(ctx, evs[0]?.sTriple), ctx.time.zeroBeat);
}

/** Cursor function of forward play: the longest keyframe prefix (from index 1) with all times <= t. */
function cursorFn(kfs: readonly Kf[]): (t: number) => number {
  const prefixMax: number[] = [];
  let m = -Infinity;
  for (let j = 1; j < kfs.length; j++) {
    m = Math.max(m, kfs[j]?.time ?? m);
    prefixMax.push(m);
  }
  return (t: number): number => {
    // largest j (1-based) with prefixMax[j - 1] <= t
    let lo = 0;
    let hi = prefixMax.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if ((prefixMax[mid] ?? Infinity) <= t) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
}

/** The piece of the cursor at keyframe index `i` (the first keyframe when out of range). */
function pieceOf(kfs: readonly Kf[], i: number): Piece {
  const index = i < kfs.length ? i : 0;
  const kf = kfs[index];
  if (kf === undefined) throw new RangeError('pieceOf: no keyframes');
  if (kf.role === 'start' && kf.ev !== null) return { type: 'curve', ev: kf.ev };
  return { type: 'hold', value: kf.value, kf, last: index === kfs.length - 1 };
}

/** True when a held text shows without its %P% marks here (a later keyframe exists), unlike its raw value. */
function holdStripsMarks(p: Piece): boolean {
  return p.type === 'hold' && !p.last && typeof p.value === 'string' && p.value.includes(COUNTER_MARK);
}

/** What a hold shows (F§9.3): a held text is tweened at progress 0 towards a next keyframe, if there is one. */
function holdShows(p: Extract<Piece, { type: 'hold' }>): ListValue {
  return holdStripsMarks(p) ? stripCounterMarks(p.value as string) : p.value;
}

function piecesEqual(a: Piece, b: Piece): boolean {
  if (a.type === 'curve' && b.type === 'curve') return a.ev === b.ev;
  if (a.type === 'hold' && b.type === 'hold') return valuesEqual(holdShows(a), holdShows(b));
  return false;
}

interface Run {
  readonly piece: Piece;
  /** Start time (-Infinity for the region before every keyframe) and its keyframe. */
  readonly start: number;
  readonly startKf: Kf | null;
  /** End time (+Infinity after the last keyframe). */
  readonly end: number;
}

function uniqueTimes(kfs: readonly Kf[]): { readonly times: number[]; readonly at: Map<number, Kf> } {
  const at = new Map<number, Kf>();
  for (const kf of kfs) if (!at.has(kf.time)) at.set(kf.time, kf);
  const times = [...at.keys()].sort((x, y) => x - y);
  return { times, at };
}

/** Maximal runs of equal pieces over the whole time axis, in time order. */
function runsOf(kfs: readonly Kf[]): Run[] {
  const kf0 = kfs[0];
  if (kf0 === undefined) return [];
  const cursor = cursorFn(kfs);
  const { times, at } = uniqueTimes(kfs);
  const runs: Run[] = [];
  const push = (piece: Piece, start: number, startKf: Kf | null, end: number): void => {
    const last = runs[runs.length - 1];
    if (last !== undefined && piecesEqual(last.piece, piece)) {
      runs[runs.length - 1] = { ...last, end };
      return;
    }
    runs.push({ piece, start, startKf, end });
  };
  push(pieceOf(kfs, 0), -Infinity, null, times[0] ?? Infinity);
  times.forEach((t, k) => {
    push(pieceOf(kfs, cursor(t)), t, at.get(t) ?? null, times[k + 1] ?? Infinity);
  });
  return runs;
}

/**
 * A constant event's curve shows its value wherever its progress is in range (t >= its start): there it is the
 * same as holding that value (strings only without %P%, whose hold and curve displays differ).
 */
function canonical(piece: Piece, t: number): Piece {
  if (piece.type !== 'curve' || !isConstant(piece.ev) || t < piece.ev.t0) return piece;
  const v = piece.ev.v0;
  if (typeof v === 'string' && v.includes(COUNTER_MARK)) return piece;
  return {
    type: 'hold',
    value: v,
    kf: { time: piece.ev.t0, beat: piece.ev.start, triple: piece.ev.sTriple, ev: piece.ev, role: 'end', value: v },
    last: false,
  };
}

/** True when two keyframe lists play identically in Phira from t = 0 on (incl. the t = 0 first frame). */
function playIdentically(a: readonly Kf[], b: readonly Kf[]): boolean {
  const a0 = a[0];
  const b0 = b[0];
  if (a0 === undefined || b0 === undefined) return a0 === b0;
  // Phira's first evaluation at t = 0 keeps cursor 0 ("time unchanged" early return).
  if (!piecesEqual(canonical(pieceOf(a, 0), 0), canonical(pieceOf(b, 0), 0))) return false;
  const ca = cursorFn(a);
  const cb = cursorFn(b);
  const times = [...new Set([...a.map((k) => k.time), ...b.map((k) => k.time)])].sort((x, y) => x - y);
  for (let k = 0; k < times.length; k++) {
    const t = times[k] ?? 0;
    const end = times[k + 1] ?? Infinity;
    if (end <= 0) continue;
    // The interval [t, end) seen from its part at or after 0.
    const from = Math.max(t, 0);
    const pa = canonical(pieceOf(a, ca(t)), from);
    const pb = canonical(pieceOf(b, cb(t)), from);
    if (!piecesEqual(pa, pb)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// Rebuilding a list from its pieces
// ---------------------------------------------------------------------------------------------------------------

interface Rebuilt {
  readonly out: OutEv[];
  /** Source events that survive (intact, sliced or baked). */
  readonly emitted: Set<Ev>;
  slicesExact: number;
  slicesApprox: number;
  fills: number;
  /** Jumps to a %P% counter text: Phira shows such text raw after its last keyframe, the jump shows it counted. */
  counterFills: number;
  /**
   * Held %P% texts that Phira showed without their marks (a later keyframe followed) but the output shows raw,
   * because no later event survives (e.g. a dropped zero-length event repeating the value).
   */
  counterHolds: number;
  /** Cut events whose curve gives Phira no number (degenerate clamped slice): kept with their own curve. */
  noNumber: number;
  /** Final jumps at the very last beat of the range: no later beat exists for the event that would hold them. */
  tailsLost: number;
  firstBad: Ev | null;
}

function intact(e: Ev): OutEv {
  return {
    sTriple: e.sTriple,
    sBeat: e.start,
    eTriple: e.eTriple,
    eBeat: e.end,
    t0: e.t0,
    t1: e.t1,
    v0: e.v0,
    v1: e.v1,
    easingType: e.easingType,
    easingLeft: e.easingLeft,
    easingRight: e.easingRight,
    bezier: e.bezier,
    bezierPoints: e.bezierPoints,
    linkgroup: e.linkgroup,
    extra: e.extra,
    origin: 'segment',
  };
}

function constantEvent(
  v: ListValue,
  s: { readonly triple: Triple; readonly beat: Beat; readonly time: number },
  e: { readonly triple: Triple; readonly beat: Beat; readonly time: number },
): OutEv {
  return {
    sTriple: s.triple,
    sBeat: s.beat,
    eTriple: e.triple,
    eBeat: e.beat,
    t0: s.time,
    t1: e.time,
    v0: v,
    v1: v,
    easingType: 1,
    easingLeft: 0,
    easingRight: 1,
    bezier: 0,
    bezierPoints: ZERO_POINTS,
    linkgroup: 0,
    extra: [],
    // Imported events are all 'segment': the origin is not part of the JSON, so normalisation stays idempotent.
    origin: 'segment',
  };
}

function easeFields(
  e: Ev,
  ease: EaseValue,
): Pick<OutEv, 'easingType' | 'easingLeft' | 'easingRight' | 'bezier' | 'bezierPoints'> {
  if (ease.kind === 'bezier') {
    return {
      easingType: e.easingType,
      easingLeft: e.easingLeft,
      easingRight: e.easingRight,
      bezier: e.bezier === 0 ? 1 : e.bezier,
      bezierPoints: ease.p,
    };
  }
  if (ease.kind === 'tween') {
    return {
      easingType: ease.id,
      easingLeft: ease.left,
      easingRight: ease.right,
      bezier: 0,
      bezierPoints: e.bezierPoints,
    };
  }
  return { easingType: 1, easingLeft: 0, easingRight: 1, bezier: 0, bezierPoints: ZERO_POINTS };
}

/** Baking: pieces double from the first count until the error is within the limit or the maximum is reached. */
const BAKE_MIN_PIECES = 8;
const BAKE_MAX_PIECES = 256;
/** Allowed deviation of a baked curve: 0.1 % of the event's value range (D§6.4 'approx'). */
const BAKE_REL_ERROR = 1e-3;

/**
 * The right part of an event whose curve gives Phira no number (a degenerate clamped slice divides by zero,
 * F§6.6): no value can stand for NaN / infinity, so the event keeps its own values and easing from `at` on, and
 * Phira shows no proper value there, as in the source. Reported 'changed' (the exact infinities may differ).
 */
function keepCurve(e: Ev, at: Pick<OutEv, 'sTriple' | 'sBeat' | 't0'>, acc: Rebuilt): OutEv[] {
  acc.noNumber++;
  return [{ ...intact(e), ...at }];
}

/** The right part of event `e` from keyframe `a` (inside the event) to its end. */
function sliceFrom(e: Ev, a: Kf, ctx: ListContext, acc: Rebuilt): OutEv[] {
  const linear = ctx.mode === 'speedLegacy';
  const p = (a.time - e.t0) / (e.t1 - e.t0);
  const base = { eTriple: e.eTriple, eBeat: e.end, t1: e.t1, v1: e.v1, linkgroup: e.linkgroup, extra: e.extra };
  const at = { sTriple: a.triple, sBeat: a.beat, t0: a.time };
  if (isConstant(e)) {
    acc.slicesExact++;
    return [{ ...intact(e), ...at }];
  }
  if (linear) {
    acc.slicesExact++;
    const v = valueAt(e, p, true).v;
    return [
      {
        ...base,
        ...at,
        v0: v,
        easingType: 1,
        easingLeft: 0,
        easingRight: 1,
        bezier: 0,
        bezierPoints: ZERO_POINTS,
        origin: 'segment',
      },
    ];
  }
  const value = valueAt(e, p, false);
  if (!value.finite) return keepCurve(e, at, acc);
  let split: SplitResult | null;
  try {
    split = p > 0 && p < 1 ? Ease.splitAt(modelEase(e), p) : null;
  } catch {
    split = null;
  }
  const rightNeedsBake = split === null || (split.exactness === 'bake' && (split.bakeHalves ?? []).includes('right'));
  if (rightNeedsBake && ctx.kind !== 'string') return bake(e, a, ctx, acc);
  const exactCurve =
    split !== null && (split.exactness === 'exact' || (split.exactness === 'bake' && split.maxRelError <= 1e-5));
  if (exactCurve && value.exact) acc.slicesExact++;
  else acc.slicesApprox++;
  const fields = split === null ? easeFields(e, modelEase(e)) : easeFields(e, split.right);
  return [{ ...base, ...at, v0: value.v, ...fields, origin: 'segment' }];
}

interface BakeNode {
  readonly beat: Beat;
  readonly triple: Triple;
  readonly time: number;
  readonly v: ListValue;
}

/** Numeric channels of a value (colours per channel); strings are never baked. */
function channels(v: ListValue): readonly number[] {
  return typeof v === 'number' ? [v] : typeof v === 'string' ? [] : v;
}

/**
 * Linear pieces approximating the right part of `e` from `a` (the sliced curve has no Phira representation, e.g.
 * a degenerate clamped slice). Equal beat subdivisions double until every piece's midpoint is within 0.1 % of the
 * value range of the original curve. Subdivision beats are exact rationals between `a` and the event's end
 * (normRange.ts `beatBetween`); one the IR cannot hold is left out, which only makes that piece longer.
 */
function bake(e: Ev, a: Kf, ctx: ListContext, acc: Rebuilt): OutEv[] {
  const progress = (time: number): number => (time - e.t0) / (e.t1 - e.t0);
  const head: BakeNode = { beat: a.beat, triple: a.triple, time: a.time, v: valueAt(e, progress(a.time), false).v };
  const tail: BakeNode = { beat: e.end, triple: e.eTriple, time: e.t1, v: e.v1 };
  const nodesFor = (n: number): BakeNode[] => {
    const nodes: BakeNode[] = [head];
    for (let j = 1; j < n; j++) {
      const beat = beatBetween(a.beat, e.end, j, n, ctx.time.shift);
      const prev = nodes[nodes.length - 1];
      if (beat === null || prev === undefined || !Beats.lt(prev.beat, beat) || !Beats.lt(beat, e.end)) continue;
      const triple = Beats.toTriple(beat);
      const time = ctx.time.sec(triple);
      nodes.push({ beat, triple, time, v: valueAt(e, progress(time), false).v });
    }
    nodes.push(tail);
    return nodes;
  };
  const range = Math.max(...channels(e.v0).map((c, i) => Math.abs((channels(e.v1)[i] ?? c) - c)), 1e-9);
  let nodes: BakeNode[] = [];
  for (let n = BAKE_MIN_PIECES; n <= BAKE_MAX_PIECES; n *= 2) {
    nodes = nodesFor(n);
    // A curve without a number somewhere has no straight-piece form either.
    if (nodes.some((node) => channels(node.v).some((c) => !Number.isFinite(c)))) {
      return keepCurve(e, { sTriple: a.triple, sBeat: a.beat, t0: a.time }, acc);
    }
    let worst = 0;
    for (let j = 0; j + 1 < nodes.length; j++) {
      const s = nodes[j];
      const t = nodes[j + 1];
      if (s === undefined || t === undefined) continue;
      const mid = (s.time + t.time) / 2;
      const exact = channels(valueAt(e, progress(mid), false).v);
      const lin = channels(s.v).map((c, i) => (c + (channels(t.v)[i] ?? c)) / 2);
      exact.forEach((c, i) => {
        worst = Math.max(worst, Math.abs(c - (lin[i] ?? c)));
      });
    }
    // Colours are whole numbers: a channel cannot be closer than its rounding.
    const limit = Math.max(BAKE_REL_ERROR * range, typeof e.v0 === 'number' ? 0 : 1);
    if (!(worst > limit)) break;
  }
  acc.slicesApprox++;
  const out: OutEv[] = [];
  for (let j = 0; j + 1 < nodes.length; j++) {
    const s = nodes[j];
    const t = nodes[j + 1];
    if (s === undefined || t === undefined || !Beats.lt(s.beat, t.beat)) continue;
    out.push({
      sTriple: s.triple,
      sBeat: s.beat,
      eTriple: t.triple,
      eBeat: t.beat,
      t0: s.time,
      t1: t.time,
      v0: s.v,
      v1: t.v,
      easingType: 1,
      easingLeft: 0,
      easingRight: 1,
      bezier: 0,
      bezierPoints: ZERO_POINTS,
      linkgroup: e.linkgroup,
      extra: e.extra,
      origin: 'segment',
    });
  }
  return out;
}

/**
 * End of the event that holds a final jump's value: 1 beat after it, or a fraction of a beat at the end of the
 * beat range (normRange.ts). null when no later beat can be written (the jump is at the very last beat).
 */
function tailEnd(
  kf: { readonly beat: Beat },
  ctx: ListContext,
): { readonly triple: Triple; readonly beat: Beat; readonly time: number } | null {
  const beat = beatAfter(kf.beat, ctx.time.shift);
  if (beat === null) return null;
  const triple = Beats.toTriple(beat);
  return { triple, beat, time: ctx.time.sec(triple) };
}

function newRebuilt(): Rebuilt {
  return {
    out: [],
    emitted: new Set(),
    slicesExact: 0,
    slicesApprox: 0,
    fills: 0,
    counterFills: 0,
    counterHolds: 0,
    noNumber: 0,
    tailsLost: 0,
    firstBad: null,
  };
}

function rebuild(sorted: readonly Ev[], ctx: ListContext): Rebuilt {
  const kfs = keyframes(sorted, withDefault(ctx, sorted[0]?.sTriple), ctx.time.zeroBeat);
  const runs = runsOf(kfs);
  const { at } = uniqueTimes(kfs);
  const acc = newRebuilt();
  // The output holds its last end value through a run it skips. A held %P% text shows without its marks only
  // while a later event follows (F§9.3), so such a skipped run needs one more event in the output.
  let strippedHoldPending = false;
  for (const run of runs) {
    const piece = run.piece;
    if (piece.type === 'curve') {
      const e = piece.ev;
      if (!(e.t1 > e.t0) || !Beats.lt(e.start, e.end)) {
        // Only the first event can be the cursor's curve without positive length (before its end): Phira shows
        // NaN / infinity there. Nothing can represent that.
        acc.firstBad ??= e;
        continue;
      }
      if (run.startKf === null || Beats.lte(run.startKf.beat, e.start)) acc.out.push(intact(e));
      else acc.out.push(...sliceFrom(e, run.startKf, ctx, acc));
      acc.emitted.add(e);
      strippedHoldPending = false;
      continue;
    }
    if (piece.kf.role === 'default' && acc.out.length === 0) continue;
    const last = acc.out[acc.out.length - 1];
    if (last !== undefined && valuesEqual(last.v1, piece.value)) {
      if (holdStripsMarks(piece)) strippedHoldPending = true;
      continue;
    }
    const startKf = run.startKf;
    if (startKf === null) continue;
    const endKf = Number.isFinite(run.end) ? at.get(run.end) : undefined;
    const end = endKf ?? tailEnd(startKf, ctx);
    if (end === null) {
      acc.tailsLost++;
      continue;
    }
    acc.out.push(constantEvent(piece.value, startKf, end));
    acc.fills++;
    strippedHoldPending = false;
    if (typeof piece.value === 'string' && piece.value.includes(COUNTER_MARK)) acc.counterFills++;
  }
  if (strippedHoldPending) acc.counterHolds++;
  trimTimeCollisions(acc.out);
  return acc;
}

/**
 * Pieces follow each other in TIME; two different beats can have the same f64 seconds (beats near 2^31 differing
 * by less than the f64 spacing there). An event can then start at an earlier beat than the previous one ends, at
 * the same time. Its start moves to the previous end: the same seconds, so Phira's progress and values are
 * unchanged; one left without length lasted no time at all and is removed (the next event takes over at that same
 * moment).
 */
function trimTimeCollisions(out: OutEv[]): void {
  for (let i = 1; i < out.length; i++) {
    const prev = out[i - 1];
    const cur = out[i];
    if (prev === undefined || cur === undefined || !Beats.lt(cur.sBeat, prev.eBeat)) continue;
    if (!Beats.lt(prev.eBeat, cur.eBeat)) {
      out.splice(i, 1);
      i--;
      continue;
    }
    out[i] = { ...cur, sTriple: prev.eTriple, sBeat: prev.eBeat, t0: prev.t1 };
  }
}

/** Value of a list at chart time 0 as Phira's never-advanced incline track sees it (cursor 0, F§9.4). */
function frozenValue(kfs: readonly Kf[]): ListValue | null {
  const k0 = kfs[0];
  if (k0 === undefined) return null;
  const k1 = kfs[1];
  if (k1 === undefined || k0.role !== 'start' || k0.ev === null) return k0.value;
  const e = k0.ev;
  return valueAt(e, (0 - k0.time) / (k1.time - k0.time), false).v;
}

function evOfOut(o: OutEv, idx: number): Ev {
  return {
    idx,
    start: o.sBeat,
    end: o.eBeat,
    sTriple: o.sTriple,
    eTriple: o.eTriple,
    t0: o.t0,
    t1: o.t1,
    v0: o.v0,
    v1: o.v1,
    easingType: o.easingType,
    easingLeft: o.easingLeft,
    easingRight: o.easingRight,
    bezier: o.bezier,
    bezierPoints: o.bezierPoints,
    linkgroup: o.linkgroup,
    extra: o.extra,
  };
}

/** Output triple of an output event's start (the beat shift applied), as the exported file holds it. */
function outStartTriple(o: OutEv, ctx: ListContext): Triple {
  return ctx.time.out(o.sTriple, o.sBeat);
}

/** Keyframes of the EXPORTED list as Phira builds them (default by the exported first triple). */
function outKeyframes(out: readonly OutEv[], ctx: ListContext): Kf[] {
  const first = out[0];
  const def = withDefault(ctx, first === undefined ? undefined : outStartTriple(first, ctx));
  return keyframes(
    out.map((o, idx) => evOfOut(o, idx)),
    def,
    ctx.time.zeroBeat,
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Modes
// ---------------------------------------------------------------------------------------------------------------

/** A source beat as the user sees it after import (the beat shift applied). */
function beatText(b: Beat, ctx: ListContext): string {
  return beatLabel(shiftedBeat(b, ctx.time.shift) ?? b);
}

function reportRebuild(acc: Rebuilt, sortedCount: number, ctx: ListContext): void {
  const r = ctx.report;
  if (acc.firstBad !== null) {
    const beat = beatText(acc.firstBad.start, ctx);
    if (ctx.mode === 'speedLegacy' && acc.firstBad.t0 === 0) {
      r.add('changed', { key: 'rpe.report.firstSpeedZeroLength' }, ctx.path, ctx.line);
    } else {
      r.add('changed', { key: 'rpe.report.firstEventZeroLength', params: { beat } }, ctx.path, ctx.line);
    }
  }
  if (acc.slicesExact > 0)
    r.add('normalized', { key: 'rpe.report.overlapSliced' }, ctx.path, ctx.line, acc.slicesExact);
  if (acc.slicesApprox > 0) r.add('changed', { key: 'rpe.report.overlapApprox' }, ctx.path, ctx.line, acc.slicesApprox);
  if (acc.noNumber > 0) r.add('changed', { key: 'rpe.report.curveNoNumber' }, ctx.path, ctx.line, acc.noNumber);
  const asJumps = sortedCount - acc.emitted.size - (acc.firstBad === null ? 0 : 1);
  if (asJumps > 0) r.add('normalized', { key: 'rpe.report.eventsAsJumps' }, ctx.path, ctx.line, asJumps);
  if (acc.counterFills > 0) {
    r.add('changed', { key: 'rpe.report.textCounterJump' }, ctx.path, ctx.line, acc.counterFills);
  }
  if (acc.counterHolds > 0) {
    r.add('changed', { key: 'rpe.report.textCounterHold' }, ctx.path, ctx.line, acc.counterHolds);
  }
  reportTailsLost(acc.tailsLost, ctx);
}

function reportTailsLost(count: number, ctx: ListContext): void {
  if (count > 0) ctx.report.add('changed', { key: 'rpe.report.jumpAtRangeEnd' }, ctx.path, ctx.line, count);
}

function standard(evs: readonly Ev[], ctx: ListContext): OutEv[] {
  const unsorted = isUnsorted(evs);
  const sorted = unsorted ? stableSortByStart(evs) : evs;
  const acc = rebuild(sorted, ctx);
  // Incline: only the value Phira freezes at t = 0 matters; normalizeEventList reports it.
  if (ctx.mode === 'incline') return acc.out;
  if (unsorted) {
    if (ctx.mode === 'speedLegacy') {
      ctx.report.add('changed', { key: 'rpe.report.speedUnsorted' }, ctx.path, ctx.line);
    } else {
      const same = playIdentically(
        sourceKeyframes(evs, ctx),
        keyframes(sorted, withDefault(ctx, sorted[0]?.sTriple), ctx.time.zeroBeat),
      );
      ctx.report.add(
        same ? 'normalized' : 'changed',
        same ? { key: 'rpe.report.unsortedSame' } : { key: 'rpe.report.unsortedChanged' },
        ctx.path,
        ctx.line,
      );
    }
  }
  reportRebuild(acc, sorted.length, ctx);
  return acc.out;
}

/** Phira's 1.7 speed integrator (F§7.3): sort by start, compress overlaps, zero-length = jump to its end speed. */
function speed170(evs: readonly Ev[], ctx: ListContext): OutEv[] {
  if (isUnsorted(evs)) ctx.report.add('normalized', { key: 'rpe.report.speedUnsorted170' }, ctx.path, ctx.line);
  const sorted = stableSortByStart(evs);
  interface At {
    readonly beat: Beat;
    readonly triple: Triple;
    readonly time: number;
  }
  type Item =
    | { readonly kind: 'event'; readonly ev: OutEv; readonly at: At }
    | { readonly kind: 'jump'; readonly at: At; readonly value: ListValue };
  const items: Item[] = [];
  const zero = ctx.time.zeroBeat;
  let cursor: At = { beat: zero, triple: Beats.toTriple(zero), time: 0 };
  let compressed = 0;
  let jumps = 0;
  for (const e of sorted) {
    const aIsCursor = Beats.lt(e.start, cursor.beat);
    const a: At = aIsCursor ? cursor : { beat: e.start, triple: e.sTriple, time: e.t0 };
    const b: At = Beats.lt(e.end, a.beat) ? a : { beat: e.end, triple: e.eTriple, time: e.t1 };
    if (!(b.time - a.time > 1e-5) || !Beats.lt(a.beat, b.beat)) {
      // Phira skips the event's keyframes but still takes its end speed for the following gap.
      items.push({ kind: 'jump', at: a, value: e.v1 });
      jumps++;
    } else {
      if (aIsCursor) compressed++;
      items.push({ kind: 'event', ev: { ...intact(e), sTriple: a.triple, sBeat: a.beat, t0: a.time }, at: a });
    }
    cursor = b;
  }
  // Items are in non-decreasing beat order (each starts at max(its event's start, the previous cursor) and the
  // cursor never moves back), so "anything later at the same beat" is the next item.
  const out: OutEv[] = [];
  let tailsLost = 0;
  items.forEach((it, i) => {
    if (it.kind === 'event') {
      out.push(it.ev);
      return;
    }
    // Anything later at the same beat wins (a later jump's speed, or an event's own speed from its start).
    const next = items[i + 1];
    if (next !== undefined && Beats.eq(next.at.beat, it.at.beat)) return;
    const last = out[out.length - 1];
    if (last !== undefined && valuesEqual(last.v1, it.value)) return;
    const end = next === undefined ? tailEnd(it.at, ctx) : next.at;
    if (end === null) {
      tailsLost++;
      return;
    }
    out.push(constantEvent(it.value, it.at, end));
  });
  if (compressed > 0)
    ctx.report.add('normalized', { key: 'rpe.report.speedCompressed' }, ctx.path, ctx.line, compressed);
  if (jumps > 0) ctx.report.add('normalized', { key: 'rpe.report.eventsAsJumps' }, ctx.path, ctx.line, jumps);
  reportTailsLost(tailsLost, ctx);
  return out;
}

/**
 * GIF progress lists (F§9.7, D§6.2). Between events and after the last one Phira plays the GIF on at its natural
 * speed from the last value, so:
 *  - an overlapping later event continues on its own curve from the earlier event's end (sliced, as elsewhere);
 *  - an event with no length (or reversed) makes Phira jump to its end value at its start and play on from
 *    there; one nested in an earlier event does the same when the earlier event ends. Such a jump is invisible
 *    when the next event starts at that very moment (its start keyframe wins), and a jump to 0 at time 0 before
 *    anything else is Phira's own start (it seeds the GIF at progress 0). Every other jump has no event form: a
 *    constant event would stop the GIF until its end, and natural-speed play needs the GIF's frame timing, which
 *    the chart does not contain. Those jumps are dropped and reported as 'changed' (first one by beat);
 *  - an unsorted list is sorted ('changed': Phira's natural-play keyframes make its forward play depend on the
 *    file order in ways a sorted list cannot reproduce).
 */
function gif(evs: readonly Ev[], ctx: ListContext): OutEv[] {
  const unsorted = isUnsorted(evs);
  const sorted = unsorted ? stableSortByStart(evs) : evs;
  const acc = newRebuilt();
  const jumps: { hidden: number; dropped: number; first: Beat | null; pending: Beat | null } = {
    hidden: 0,
    dropped: 0,
    /** Beat of the first dropped jump (source beats). */
    first: null,
    /** A jump not written yet: its beat (source beats). */
    pending: null,
  };
  const settle = (nextStart: Beat | null): void => {
    const pending = jumps.pending;
    if (pending === null) return;
    if (nextStart !== null && Beats.eq(pending, nextStart)) jumps.hidden++;
    else {
      jumps.dropped++;
      jumps.first ??= pending;
    }
    jumps.pending = null;
  };
  const jump = (e: Ev, at: Beat): void => {
    if (
      jumps.pending === null &&
      acc.out.length === 0 &&
      Beats.eq(at, e.start) &&
      e.t0 === 0 &&
      fr(e.v1 as number) === 0
    ) {
      // A jump to progress 0 at time 0 before anything else: exactly where Phira starts the GIF anyway.
      jumps.hidden++;
      return;
    }
    if (jumps.pending !== null) {
      // An earlier jump at the same moment is replaced by this one; at an earlier moment it was visible.
      if (Beats.eq(jumps.pending, at)) jumps.hidden++;
      else settle(null);
    }
    jumps.pending = at;
  };
  for (const e of sorted) {
    const prev = acc.out[acc.out.length - 1];
    const inside = prev !== undefined && Beats.lt(e.start, prev.eBeat);
    if (!Beats.lt(e.start, e.end)) {
      jump(e, inside ? prev.eBeat : e.start);
      continue;
    }
    if (prev !== undefined && Beats.lte(e.end, prev.eBeat)) {
      jump(e, prev.eBeat);
      continue;
    }
    if (!inside) {
      settle(e.start);
      acc.out.push(intact(e));
      acc.emitted.add(e);
      continue;
    }
    settle(prev.eBeat);
    const kf: Kf = { time: prev.t1, beat: prev.eBeat, triple: prev.eTriple, ev: e, role: 'end', value: prev.v1 };
    acc.out.push(...sliceFrom(e, kf, ctx, acc));
    acc.emitted.add(e);
  }
  settle(null);
  const r = ctx.report;
  if (unsorted) r.add('changed', { key: 'rpe.report.gifUnsorted' }, ctx.path, ctx.line);
  if (acc.slicesExact > 0) {
    r.add('normalized', { key: 'rpe.report.overlapSliced' }, ctx.path, ctx.line, acc.slicesExact);
  }
  if (acc.slicesApprox > 0) {
    r.add('changed', { key: 'rpe.report.overlapApprox' }, ctx.path, ctx.line, acc.slicesApprox);
  }
  if (acc.noNumber > 0) r.add('changed', { key: 'rpe.report.curveNoNumber' }, ctx.path, ctx.line, acc.noNumber);
  if (jumps.hidden > 0) r.add('normalized', { key: 'rpe.report.gifJumpHidden' }, ctx.path, ctx.line, jumps.hidden);
  if (jumps.first !== null) {
    r.add(
      'changed',
      { key: 'rpe.report.gifJumpDropped', params: { beat: beatText(jumps.first, ctx) } },
      ctx.path,
      ctx.line,
      jumps.dropped,
    );
  }
  return acc.out;
}

// ---------------------------------------------------------------------------------------------------------------
// Default keyframes after the beat shift
// ---------------------------------------------------------------------------------------------------------------

/** True when Phira evaluates the event's curve as a straight line (any slice of the linear easing). */
function isLinearCurve(e: Ev): boolean {
  const m = modelEase(e);
  return m.kind === 'tween' && m.id === 1;
}

/**
 * Makes the exported list get Phira's default keyframe exactly when the source got it (see the module header).
 * `out` reproduces the source as Phira plays it ASSUMING the exported list gets the same default; the beat shift
 * (or a rebuilt first event) can flip that, which changes what Phira shows at the first evaluation (t = 0, the
 * whole intro; incline keeps it forever) and before the first event.
 *  - The source had the default, the export would not (its first event now starts at beat 0): a constant event
 *    with the default value is added just before the first event. Exact.
 *  - The export would get a default the source did not have: the first event, if it runs across time 0, is cut
 *    there on its own curve, so the export starts at beat 0; if it starts after time 0, a filler event from time 0
 *    reproduces the curve Phira continued backwards before it (constant or linear curves only). Exact up to the
 *    slice's exactness.
 *  - Anything else that changes a visible value is reported ('changed').
 */
function keepDefault(out: OutEv[], evs: readonly Ev[], ctx: ListContext): OutEv[] {
  const def = ctx.def;
  const first = out[0];
  if (def === undefined || first === undefined) return out;
  const srcHas = withDefault(ctx, evs[0]?.sTriple) !== undefined;
  const outHas = withDefault(ctx, outStartTriple(first, ctx)) !== undefined;
  if (srcHas === outHas) return out;
  const firstEvalOnly = ctx.mode === 'incline';
  const zero = ctx.time.zeroBeat;
  const f = evOfOut(first, 0);
  const curveAt0 = valueAt(f, (0 - f.t0) / (f.t1 - f.t0), false).v;
  if (srcHas) {
    // Phira showed the default at the first evaluation and up to the first event; the export would not.
    if (!(first.t0 > 0 && !firstEvalOnly) && valuesEqual(curveAt0, def)) return out;
    // A pad from beat 0, or 1 beat long before a first event at beat 0 (a fraction of a beat at the start of the
    // beat range; nothing at its very first beat).
    const sBeat = Beats.lt(zero, first.sBeat) ? zero : beatBefore(first.sBeat, ctx.time.shift);
    if (sBeat === null) {
      ctx.report.add('changed', { key: 'rpe.report.defaultNotKept' }, ctx.path, ctx.line);
      return out;
    }
    const sTriple = Beats.toTriple(sBeat);
    const pad = constantEvent(
      def,
      { triple: sTriple, beat: sBeat, time: ctx.time.sec(sTriple) },
      { triple: first.sTriple, beat: first.sBeat, time: first.t0 },
    );
    ctx.report.add('normalized', { key: 'rpe.report.defaultKept' }, ctx.path, ctx.line);
    return [pad, ...out];
  }
  // The export would get a default keyframe the source did not have.
  const zeroTriple = Beats.toTriple(zero);
  if (Beats.lt(first.sBeat, zero) && Beats.lt(zero, first.eBeat)) {
    const acc = newRebuilt();
    const kf: Kf = { time: 0, beat: zero, triple: zeroTriple, ev: f, role: 'end', value: curveAt0 };
    const cut = sliceFrom(f, kf, ctx, acc);
    const exact = acc.slicesApprox === 0 && acc.noNumber === 0;
    if (exact) ctx.report.add('normalized', { key: 'rpe.report.startCut' }, ctx.path, ctx.line);
    else ctx.report.add('changed', { key: 'rpe.report.startCutApprox' }, ctx.path, ctx.line);
    return [...cut, ...out.slice(1)];
  }
  if (Beats.lt(zero, first.sBeat)) {
    const p0 = (0 - f.t0) / (f.t1 - f.t0);
    const constant = isConstant(f) && valuesEqual(curveAt0, valueAt(f, 0, false).v);
    // The filler starts at the lead-in's value at time 0: only exact when it is inside the f32 range.
    const lead = valueAt(f, p0, true);
    const linear = !constant && typeof f.v0 === 'number' && isLinearCurve(f) && Number.isFinite(curveAt0) && lead.exact;
    if (constant || linear) {
      const filler: OutEv = {
        ...constantEvent(
          constant ? f.v0 : lead.v,
          { triple: zeroTriple, beat: zero, time: 0 },
          { triple: first.sTriple, beat: first.sBeat, time: first.t0 },
        ),
        v1: f.v0,
      };
      ctx.report.add('normalized', { key: 'rpe.report.leadInFilled' }, ctx.path, ctx.line);
      return [filler, ...out];
    }
  }
  // No representation: report when a visible value changes.
  const leadInDiffers = !firstEvalOnly && first.t0 > 0;
  if (leadInDiffers || !valuesEqual(curveAt0, def)) {
    ctx.report.add('changed', { key: 'rpe.report.defaultAtStart' }, ctx.path, ctx.line);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------------------------------------------

function toIr(o: OutEv, ctx: ListContext): IrEvent<ListValue> {
  return {
    startTime: ctx.time.out(o.sTriple, o.sBeat),
    endTime: ctx.time.out(o.eTriple, o.eBeat),
    start: o.v0,
    end: o.v1,
    easingType: o.easingType,
    easingLeft: o.easingLeft,
    easingRight: o.easingRight,
    bezier: o.bezier,
    bezierPoints: o.bezierPoints,
    linkgroup: o.linkgroup,
    extra: o.extra,
    origin: o.origin,
    srcKey: null,
  };
}

/**
 * Normalises one event list. Returns the IR events (possibly empty when nothing survives: the caller then treats
 * the list as absent) and reports every change on `ctx.report`.
 */
export function normalizeEventList(events: readonly RpeEvent<ListValue>[], ctx: ListContext): IrEvent<ListValue>[] {
  const evs = coerceEvents(events, ctx);
  if (evs.length === 0) return [];
  const wellFormed = isWellFormed(evs);
  let out: OutEv[];
  if (wellFormed) out = evs.map(intact);
  else if (ctx.mode === 'speed170') out = speed170(evs, ctx);
  else if (ctx.mode === 'gif') out = gif(evs, ctx);
  else out = standard(evs, ctx);
  if (ctx.def !== undefined) out = keepDefault(out, evs, ctx);
  if (ctx.mode === 'incline' && !wellFormed) {
    // Phira never advances incline: only the value at its first evaluation (t = 0) is visible (F§9.4).
    const before = frozenValue(sourceKeyframes(evs, ctx));
    const after = frozenValue(outKeyframes(out, ctx));
    const same = before !== null && after !== null ? valuesEqual(before, after) : before === after;
    ctx.report.add(same ? 'normalized' : 'changed', { key: 'rpe.report.inclineRebuilt' }, ctx.path, ctx.line);
  }
  return out.map((o) => toIr(o, ctx));
}
